import { Percent } from '@kodiak-finance/sdk-core'
import LiquidityProviders from 'components/Settings/LiquidityProviders'
import { ProvidersConfigProps } from 'components/swap/SwapHeader'
import { useContext, useState } from 'react'
import {
  useSetUserPriceProtection,
  useSetUserSlippageTolerance,
  useUserPriceProtection,
  useUserSlippageTolerance,
} from 'state/user/hooks'
import { PRICE_PROTECTION_DEFAULT } from 'state/user/reducer'
import styled, { ThemeContext } from 'styled-components'

import { ThemedText } from '../../theme'
import { AutoColumn } from '../Column'
import QuestionHelper from '../QuestionHelper'
import { RowBetween, RowFixed } from '../Row'

enum SlippageError {
  InvalidInput = 'InvalidInput',
}

const SlippageEmojiContainer = styled.span`
  position: absolute;
  left: 8px;
  color: #f3841e;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    display: none;
  `}
`

const Option = styled.div<{ active: boolean }>`
  display: flex;
  width: auto;
  cursor: pointer;
  padding: 4px 12px;
  text-align: center;
  gap: 4px;
  border-radius: 3px;
  background: ${({ active, theme }) => (active ? theme.cardPrimary : 'transparent')};
  /* border: 1px solid ${({ theme }) => theme.backgroundOutline}; */

  pointer-events: ${({ active }) => active && 'none'};
`

const Switch = styled.div`
  display: flex;
  align-items: center;
  width: auto;
  padding: 4px;
  border-radius: 4px;
  height: 36px;
  background: ${({ theme }) => theme.cardSecondary};
`

export const Input = styled.input`
  width: 100%;
  height: 34px;
  border-radius: 4px;
  display: flex;
  flex: 1;
  font-size: 16px;
  outline: none;
  padding: 8px 24px 8px 8px;
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  background: ${({ theme }) => theme.cardSecondary};
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  text-align: right;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  ::placeholder {
    color: ${({ theme }) => theme.textSecondary};
  }
`

export const TxnInput = styled(Input)`
  text-align: left;
`

export const InputContainer = styled(RowBetween)<{ error?: boolean }>`
  position: relative;
`

export const PercentAbsolute = styled.p`
  position: absolute;
  right: 8px;
`

export const OptionColumn = styled.div`
  display: flex;
  flex-direction: column;
`

export const SlippageRow = styled(RowBetween)`
  gap: 16px;
`

interface TransactionSettingsProps {
  placeholderSlippage: Percent
  providersConfig?: ProvidersConfigProps
  isTwap: boolean
}

const MAX_SLIPPAGE_BASIS_POINTS = 2000 // 20% = 2000 basis points (2000/10000 = 0.2)

const PriceProtectionSettings = () => {
  const userPriceProtection = useUserPriceProtection()
  const setUserPriceProtection = useSetUserPriceProtection()

  const [priceProtectionInput, setPriceProtectionInput] = useState('')
  const [priceProtectionError, setPriceProtectionError] = useState<SlippageError | false>(false)
  const theme = useContext(ThemeContext)

  function parsePriceProtectionInput(value: string) {
    // populate what the user typed and clear the error
    setPriceProtectionInput(value)
    setPriceProtectionError(false)

    if (value.length === 0) {
      setUserPriceProtection('auto')
    } else {
      const parsed = Math.floor(Number.parseFloat(value) * 100)

      if (!Number.isInteger(parsed) || parsed < 0 || parsed > MAX_SLIPPAGE_BASIS_POINTS) {
        setUserPriceProtection('auto')
        if (value !== '.') {
          setPriceProtectionError(SlippageError.InvalidInput)
        }
      } else {
        setUserPriceProtection(new Percent(parsed, 10_000))
      }
    }
  }

  const [focusedUserPriceProtectionInput, setFocusedUserPriceProtectionInput] = useState(false)

  const tooLow = userPriceProtection !== 'auto' && userPriceProtection.lessThan(new Percent(1, 100))
  const tooHigh = userPriceProtection !== 'auto' && userPriceProtection.greaterThan(new Percent(20, 100))

  return (
    <>
      <RowFixed>
        <ThemedText.DeprecatedBlack fontWeight={400} fontSize={14} color={theme.deprecated_text2}>
          <>Price Protection</>
        </ThemedText.DeprecatedBlack>
        <QuestionHelper
          text={
            <>
              The protocol uses an oracle price to help protect users from unfavorable executions. If the execution
              price is worse than the oracle price by more than the allowed percentage, the transaction will not be
              executed.
            </>
          }
        />
      </RowFixed>
      <SlippageRow>
        <Switch>
          <Option
            onClick={() => {
              setUserPriceProtection('auto')
            }}
            active={userPriceProtection === 'auto' && !focusedUserPriceProtectionInput}
          >
            <>Auto</>
          </Option>
          <Option
            onClick={() => {
              // When switching to custom slippage, use `auto` value as a default.
              parsePriceProtectionInput('')
            }}
            active={userPriceProtection !== 'auto' || focusedUserPriceProtectionInput}
            warning={!!priceProtectionError}
            tabIndex={-1}
          >
            <>Custom</>
          </Option>
        </Switch>
        <InputContainer>
          {tooLow || tooHigh ? (
            <SlippageEmojiContainer>
              <span role="img" aria-label="warning">
                ⚠️
              </span>
            </SlippageEmojiContainer>
          ) : null}
          <Input
            type="number"
            placeholder={
              userPriceProtection !== 'auto' ? userPriceProtection.toFixed(2) : PRICE_PROTECTION_DEFAULT.toFixed(2)
            }
            value={
              priceProtectionInput.length > 0
                ? priceProtectionInput
                : userPriceProtection === 'auto'
                ? ''
                : userPriceProtection.toFixed(2)
            }
            onChange={(e) => parsePriceProtectionInput(e.target.value)}
            onBlur={() => {
              setPriceProtectionInput('')
              setPriceProtectionError(false)
              setFocusedUserPriceProtectionInput(false)
            }}
            onFocus={() => {
              setFocusedUserPriceProtectionInput(true)
            }}
            color={priceProtectionError ? 'red' : ''}
          />
          <PercentAbsolute>%</PercentAbsolute>
        </InputContainer>
      </SlippageRow>
    </>
  )
}
export default function TransactionSettings({
  isTwap,
  placeholderSlippage,
  providersConfig,
}: TransactionSettingsProps) {
  // const { chainId } = useConnection()
  const theme = useContext(ThemeContext)
  const userSlippageTolerance = useUserSlippageTolerance()
  const setUserSlippageTolerance = useSetUserSlippageTolerance()

  const [slippageInput, setSlippageInput] = useState('')
  const [slippageError, setSlippageError] = useState<SlippageError | false>(false)

  function parseSlippageInput(value: string) {
    // populate what the user typed and clear the error
    setSlippageInput(value)
    setSlippageError(false)

    if (value.length === 0) {
      setUserSlippageTolerance('auto')
    } else {
      const parsed = Math.floor(Number.parseFloat(value) * 100)

      if (!Number.isInteger(parsed) || parsed < 0 || parsed > MAX_SLIPPAGE_BASIS_POINTS) {
        setUserSlippageTolerance('auto')
        if (value !== '.') {
          setSlippageError(SlippageError.InvalidInput)
        }
      } else {
        setUserSlippageTolerance(new Percent(parsed, 10_000))
      }
    }
  }

  const [focusedUserSlippageInput, setFocusedUserSlippageInput] = useState(false)

  const tooLow = userSlippageTolerance !== 'auto' && userSlippageTolerance.lessThan(new Percent(5, 10_000))
  const tooHigh = userSlippageTolerance !== 'auto' && userSlippageTolerance.greaterThan(new Percent(5, 100))

  if (isTwap) {
    return <PriceProtectionSettings />
  }

  return (
    <AutoColumn gap="md">
      <AutoColumn gap="sm">
        <RowFixed>
          <ThemedText.DeprecatedBlack fontWeight={400} fontSize={14} color={theme.deprecated_text2}>
            <>Slippage Tolerance</>
          </ThemedText.DeprecatedBlack>
          <QuestionHelper
            text={<>Your transaction will revert if the price changes unfavorably by more than this percentage.</>}
          />
        </RowFixed>
        <SlippageRow>
          <Switch>
            <Option
              onClick={() => {
                setUserSlippageTolerance('auto')
              }}
              active={userSlippageTolerance === 'auto' && !focusedUserSlippageInput}
            >
              <>Auto</>
            </Option>
            <Option
              onClick={() => {
                // When switching to custom slippage, use `auto` value as a default.
                parseSlippageInput('')
              }}
              active={userSlippageTolerance !== 'auto' || focusedUserSlippageInput}
              warning={!!slippageError}
              tabIndex={-1}
            >
              <>Custom</>
            </Option>
          </Switch>
          <InputContainer>
            {tooLow || tooHigh ? (
              <SlippageEmojiContainer>
                <span role="img" aria-label="warning">
                  ⚠️
                </span>
              </SlippageEmojiContainer>
            ) : null}
            <Input
              type="number"
              placeholder={placeholderSlippage.toFixed(2)}
              value={
                slippageInput.length > 0
                  ? slippageInput
                  : userSlippageTolerance === 'auto'
                  ? ''
                  : userSlippageTolerance.toFixed(2)
              }
              onChange={(e) => parseSlippageInput(e.target.value)}
              onBlur={() => {
                setSlippageInput('')
                setSlippageError(false)
                setFocusedUserSlippageInput(false)
              }}
              onFocus={() => {
                setFocusedUserSlippageInput(true)
              }}
              color={slippageError ? 'red' : ''}
            />
            <PercentAbsolute>%</PercentAbsolute>
          </InputContainer>
        </SlippageRow>
        <LiquidityProviders
          isLoading={providersConfig?.isLoading || false}
          availableList={providersConfig?.availableProviders}
          selectedList={providersConfig?.selectedProviders}
          onSelect={providersConfig?.onSelect}
          onReset={providersConfig?.onReset}
          onSelectAll={providersConfig?.onSelectAll}
        />
        {slippageError || tooLow || tooHigh ? (
          <RowBetween
            style={{
              fontSize: '14px',
              paddingTop: '7px',
              color: slippageError ? 'red' : '#F3841E',
            }}
          >
            {slippageError ? (
              <>Enter a valid slippage percentage (max 20%)</>
            ) : tooLow ? (
              <>Your transaction may fail</>
            ) : (
              <>Your transaction may be frontrun</>
            )}
          </RowBetween>
        ) : null}
      </AutoColumn>
    </AutoColumn>
  )
}
